import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { twMerge } from 'tailwind-merge'
import { Disclosure } from '@headlessui/react'
import { ChevronUpIcon } from '@heroicons/react/solid'
import { useCookie, useConfig } from 'msp-integrations'
import Modal from '@molecules/Modal/Modal'
import { Button } from '@atoms/Button'
import Checkbox from '@atoms/Checkbox/Checkbox'
import CustomLinkComponent from '@atoms/CustomLink/CustomLinkComponent'
import { cookieBannerMessages as messages } from './CookieModal.messages'
import { Events, EventsEmitter } from '@utils/eventsEmitter'

export type CookieValues = {
  required: boolean
  functional: boolean
  performance: boolean
  marketing: boolean
}

type CookieModalProps = {
  className?: string
  isDarkMode?: boolean
}

type CookieItemType = {
  name: string
  label: { id: string; defaultMessage: string }
  value: boolean
  onChange: () => void
  copy: { id: string; defaultMessage: string }
  disabled?: boolean
}

const CookieModal = ({ className, isDarkMode = false }: CookieModalProps) => {
  const { formatMessage } = useIntl()
  const location = useLocation()
  const isImprintOrPrivacyPage =
    location.pathname?.endsWith(formatMessage(messages.imprintPageLink)) ||
    location.pathname?.endsWith(formatMessage(messages.dataProtectionPageLink))
  const {
    theme: { font, fontVariant }
  } = useConfig()
  const {
    isModalOpen,
    isConfirmed,
    showDetails,
    isFunctionalAllowed,
    isPerformanceAllowed,
    isMarketingAllowed,
    setCookiesAllowed,
    setCookieModal
  } = useCookie()
  const [isFunctionalCookieSelected, setIsFunctionalCookieSelected] =
    useState(isFunctionalAllowed)
  const [isPerformanceCookieSelected, setIsPerformanceCookieSelected] =
    useState(isPerformanceAllowed)
  const [isMarketingCookieSelected, setIsMarketingCookieSelected] =
    useState(isMarketingAllowed)
  const [isDetailsOpen, setIsDetailsOpen] = useState(showDetails)

  useEffect(() => {
    if (isFunctionalAllowed) setIsFunctionalCookieSelected(true)
    if (isPerformanceAllowed) setIsPerformanceCookieSelected(true)
    if (isMarketingAllowed) setIsMarketingCookieSelected(true)
  }, [isFunctionalAllowed, isPerformanceAllowed, isMarketingAllowed])

  useEffect(() => {
    setIsDetailsOpen(showDetails)
  }, [showDetails])

  const acceptAllCookies = () => {
    const allowedCookies = {
      marketing: true,
      performance: true,
      functional: true,
      required: true
    }

    setCookiesAllowed(allowedCookies)

    EventsEmitter.dispatch(Events.CHANGED_CONSENTS, allowedCookies)
  }

  const rejectAllCookies = () => {
    const allowedCookies = {
      marketing: false,
      performance: false,
      functional: false,
      required: true
    }

    setCookiesAllowed(allowedCookies)

    EventsEmitter.dispatch(Events.CHANGED_CONSENTS, allowedCookies)
  }

  const updateCookieSettings = () => {
    const allowedCookies = {
      functional: isFunctionalCookieSelected,
      performance: isPerformanceCookieSelected,
      marketing: isMarketingCookieSelected,
      required: true
    }

    setCookiesAllowed(allowedCookies)

    EventsEmitter.dispatch(Events.CHANGED_CONSENTS, allowedCookies)
  }

  const ModalHeader = () => (
    <div className={twMerge(fontVariant.medium, 'text-24 pb-4')}>
      {formatMessage(messages.cookieModal)}
    </div>
  )

  const cookieItems: CookieItemType[] = [
    {
      name: 'required',
      label: messages.requiredCookieCheckboxText,
      value: true,
      onChange: () => {},
      copy: messages.requiredCookieCopyText,
      disabled: true
    },
    {
      name: 'functional',
      label: messages.functionalCookieCheckboxText,
      value: isFunctionalCookieSelected,
      onChange: () =>
        setIsFunctionalCookieSelected(!isFunctionalCookieSelected),
      copy: messages.functionalCookieCopyText
    },
    {
      name: 'perfomance',
      label: messages.perfomanceRelatedCookieCheckboxText,
      value: isPerformanceCookieSelected,
      onChange: () =>
        setIsPerformanceCookieSelected(!isPerformanceCookieSelected),
      copy: messages.perfomanceRelatedCookieCopyText
    },
    {
      name: 'marketing',
      label: messages.marketingRelatedCookieCheckboxText,
      value: isMarketingCookieSelected,
      onChange: () => setIsMarketingCookieSelected(!isMarketingCookieSelected),
      copy: messages.marketingRelatedCookieCopyText
    }
  ]

  const ModalBody = () => (
    <div
      className={twMerge(
        font.regular,
        'text-14 heading-24 space-y-2 overflow-auto font-thin'
      )}
    >
      <div>{formatMessage(messages.body)}</div>
      <div>{formatMessage(messages.recommendation)}</div>
      <div>{formatMessage(messages.accept)}</div>
      <div
        className={`mt-3 flex flex-row items-center space-x-3 ${
          isDetailsOpen ? 'pb-4' : 'pb-10'
        }`}
      >
        <CustomLinkComponent
          className='font-12 capitalize underline underline-offset-2'
          inline
          href={formatMessage(messages.imprintPageLink)}
          colorScheme={isDarkMode ? 'secondary' : 'primary'}
        >
          {formatMessage(messages.imprintLinkText)}
        </CustomLinkComponent>
        <span
          className={twMerge(
            'inline-block h-3  border-l',
            isDarkMode ? 'border-white' : 'border-black'
          )}
        />
        <CustomLinkComponent
          className='font-12 capitalize underline underline-offset-2'
          inline
          href={formatMessage(messages.dataProtectionPageLink)}
          colorScheme={isDarkMode ? 'secondary' : 'primary'}
        >
          {formatMessage(messages.dataProtectionLinkText)}
        </CustomLinkComponent>
      </div>
      {isDetailsOpen &&
        cookieItems.map((item) => (
          <Disclosure key={`${item.name}-accordion-item`}>
            {({ open }) => (
              <>
                <div className='justify-left flex flex-row items-center font-bold'>
                  <Checkbox
                    name={item.name}
                    label={formatMessage(item.label)}
                    value={item.value}
                    disabled={item.disabled}
                    onChange={() => item.onChange()}
                    isDarkMode={isDarkMode}
                  />
                  <Disclosure.Button>
                    <ChevronUpIcon
                      className={twMerge(
                        'ml-2 h-5 w-5',
                        open ? '' : 'rotate-180 transform'
                      )}
                    />
                  </Disclosure.Button>
                </div>
                <Disclosure.Panel className='text-14 my-2 font-light'>
                  {formatMessage(item.copy)}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        ))}
    </div>
  )

  const handleClose = () => {
    isConfirmed && setCookieModal(false)
  }

  const ModalFooter = () => (
    <div className='flex w-full flex-col-reverse items-center justify-center space-x-0 space-y-4 space-y-reverse pt-4 md:flex-row md:space-x-4 md:space-y-0'>
      {isDetailsOpen && (
        <Button
          className='w-full md:w-auto'
          variant={isDarkMode ? 'outline' : 'default'}
          isDarkMode={isDarkMode}
          onClick={() => updateCookieSettings()}
        >
          {formatMessage(messages.confirmSelectionButton)}
        </Button>
      )}
      {!isDetailsOpen && (
        <Button
          className='w-full md:w-auto'
          variant={isDarkMode ? 'outline' : 'default'}
          isDarkMode={isDarkMode}
          onClick={() => setIsDetailsOpen(!isDetailsOpen)}
        >
          {formatMessage(messages.showDetailsButton)}
        </Button>
      )}
      <Button
        className='w-full md:w-auto'
        variant={isDarkMode ? 'outline' : 'default'}
        isDarkMode={isDarkMode}
        onClick={() => rejectAllCookies()}
      >
        {formatMessage(messages.rejectAllButton)}
      </Button>
      <Button
        className='w-full md:w-auto'
        variant={isDarkMode ? 'outline' : 'default'}
        isDarkMode={isDarkMode}
        onClick={() => acceptAllCookies()}
      >
        {formatMessage(messages.acceptAllButton)}
      </Button>
    </div>
  )

  return (
    <Modal
      style={twMerge(
        'xl:w-1/2 xl:max-w-4xl z-[9999] md:w-10/12 w-full',
        isDarkMode ? 'bg-black text-white fill-white' : '',
        className
      )}
      isOpen={(!isConfirmed && !isImprintOrPrivacyPage) || isModalOpen}
      overlay
      blockScroll
      hideCloseButton={!isConfirmed}
      isExtended
      hideBodyOverflow={false}
      modalHeader={<ModalHeader />}
      modalBody={<ModalBody />}
      modalFooter={<ModalFooter />}
      onClose={() => handleClose()}
      overlayClassName='z-[9998]'
    />
  )
}

export default CookieModal
